import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { groupBy, isEqual } from 'lodash';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { WebStoreEnterPurchaseClubTemplate } from '../../components/Templates';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useLocale from '../../hooks/useLocale';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { RewardOfferType, VariantsData } from '../../models/RewardStore';
import { SpaceMembershipTierSubscriptionListType } from '../../models/SpaceStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { arrayEqual } from '../../utils/array';

interface VariantInfoWithName {
  groupName: string;
  groupIndex: number;
  name: string;
  description: string;
  variant: Record<string, string>;
}

type State = {
  loadingTotal: number;
  finishFetchOffer: boolean;
};

const { fetchOffer } = useRewardStore.getState();

const WebStoreEnterPurchaseClubScreen = () => {
  const navigate = useNavigate();
  const l = useLocale();

  const { rewardOfferId } = useParams();

  const userId = useSessionStore((store) => store.activeUserId);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    finishFetchOffer: false,
  });

  useRedirectEntry();

  const rewardOfferStore = 'system';

  useRewardStoreListField('offers', `${rewardOfferStore}_${rewardOfferId}`);

  useEffect(() => {
    if (rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchOffer(rewardOfferStore, rewardOfferId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishFetchOffer: true,
          }));
        });
    }
  }, [rewardOfferId]);

  const rewardOffer = useRewardStore(
    (store) => store.getOffer(rewardOfferId ?? ''),
    shallow,
  );

  useRedirectHome(state.finishFetchOffer && !rewardOffer);

  const rewardRedemptionsFieldName = `${rewardOfferStore}_${rewardOfferId}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptionsByOfferId(
          rewardOfferStore,
          rewardOfferId ?? '',
          cursor,
        ),
    [rewardOfferId],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    rewardRedemptionsField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if (userId && rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [fetchAllRewardRedemptions, rewardOfferId, userId]);

  const rewardRedemptions = useRewardStore((rewardState) => {
    return rewardState.getRedemptions(rewardRedemptionsFieldName);
  }, arrayEqual(shallow));

  const spaceMembershipTierSubscriptionFieldName =
    SpaceMembershipTierSubscriptionListType.ActiveUser;
  const spaceMembershipTierSubscriptionField = useSpaceStoreListField(
    'spaceMembershipTierSubscriptions',
    spaceMembershipTierSubscriptionFieldName,
  );
  const fetchTierSubscriptions = useCallback<FetchListFn>(
    (cursor) => useSpaceStore.getState().fetchTierSubscriptions(cursor),
    [],
  );
  const { fetchAll: fetchAllTierSubscriptions } = useFetchList(
    spaceMembershipTierSubscriptionField,
    fetchTierSubscriptions,
  );

  useEffect(() => {
    if (userId && rewardOfferId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllTierSubscriptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [fetchAllTierSubscriptions, rewardOfferId, userId]);

  const spaceMembershipTierSubscriptions = useSpaceStore((store) => {
    return store.getTierSubscriptions(spaceMembershipTierSubscriptionFieldName);
  }, arrayEqual(shallow));

  const activatedTierSubscriptions = useMemo(() => {
    return spaceMembershipTierSubscriptions.filter(
      (subscriptions) => subscriptions.isActivated,
    );
  }, [spaceMembershipTierSubscriptions]);

  const generateVariants = useCallback((infos: VariantsData[]) => {
    const variants: VariantInfoWithName[] = [];

    const generateCombinations = (
      index: number,
      currentVariant: Record<string, string>,
      groupIndex: number | undefined,
      groupName: string | undefined,
    ) => {
      if (index === infos.length) {
        const variantName = infos
          .map((info) => {
            const value = currentVariant[info.key];
            const option = info.options.find((o) => o.value === value);
            return option?.name;
          })
          .join(' - ');
        const variantDescription = infos
          .map((info) => {
            const value = currentVariant[info.key];
            const option = info.options.find((o) => o.value === value);
            return option?.description;
          })
          .join('\n');
        const infoWithName: VariantInfoWithName = {
          name: variantName,
          description: variantDescription,
          variant: { ...currentVariant },
          groupName: groupName ?? '',
          groupIndex: groupIndex ?? -1,
        };
        variants.push(infoWithName);
        return;
      }

      infos[index]?.options.forEach((option, optionIndex) => {
        const aaa = infos[index]?.key;
        if (aaa) {
          generateCombinations(
            index + 1,
            {
              ...currentVariant,
              [aaa]: option.value,
            },
            groupIndex ?? optionIndex,
            groupName ?? infos[index]?.groupName,
          );
        }
      });
    };

    generateCombinations(0, {}, undefined, undefined);

    const groupVariants = Object.values(groupBy(variants, 'groupIndex'));
    if (groupVariants.every((variant) => variant.length === 1)) {
      return [variants];
    } else {
      return groupVariants;
    }
  }, []);

  const versions = useMemo(() => {
    if (
      rewardOffer &&
      rewardOffer?.reward.type === RewardOfferType.ClassroomMembershipTierSet
    ) {
      const variantInfos = l(rewardOffer.reward.i18nVariantsData);
      const variantItems = rewardOffer.reward.items;

      const variantInfoWithNames = variantInfos
        ? generateVariants(variantInfos).flat()
        : [];

      const filterVarinats = variantInfoWithNames.filter((variantInfo) => {
        const variantItem = variantItems.find((item) =>
          isEqual(item.variant, variantInfo.variant),
        );
        const tierSubscription = activatedTierSubscriptions.find(
          (subscription) =>
            subscription.tierId === variantItem?.tierId &&
            subscription.classroomId === variantItem.classroomId,
        );

        const completedRedemption = rewardRedemptions.find(
          (redemption) =>
            redemption.isCompleted() &&
            isEqual(redemption.variant, variantInfo.variant),
        );

        return tierSubscription && completedRedemption;
      });

      return filterVarinats.map((variantInfo) => {
        const variantItem = variantItems.find((item) =>
          isEqual(item.variant, variantInfo.variant),
        );

        const tierSubscription = activatedTierSubscriptions.find(
          (subscription) =>
            subscription.tierId === variantItem?.tierId &&
            subscription.classroomId === variantItem.classroomId,
        );

        return {
          classroomId: variantItem?.classroomId ?? '',
          title: variantInfo.name,
          description: variantInfo.description,
          expiredAt: tierSubscription?.expiredAt,
        };
      });
    }
    return [];
  }, [
    activatedTierSubscriptions,
    generateVariants,
    l,
    rewardOffer,
    rewardRedemptions,
  ]);

  const onClickEnter = useCallback(
    (classroomId: string) => {
      navigate(`/club/${classroomId}`);
    },
    [navigate],
  );

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <WebStoreEnterPurchaseClubTemplate
        versions={versions}
        onClickEnter={onClickEnter}
      />
    </>
  );
};

export default WebStoreEnterPurchaseClubScreen;
